import { ChangeDetectionStrategy, Component, computed, Inject, inject, OnInit, signal, TemplateRef, viewChild, } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { SharedModels } from '@share-models';
import { features } from '@consts/text.const';
import { PersonGroupsModel } from '@models/person-groups.model';
import { PreloaderColor } from '@enums/shared.enum';
import { AppState } from '../../../../../../store/app.state';
import { personGroupsFeature } from '@states/person-groups/person-groups.feature';
import { PersonGroupsActions } from '@states/person-groups/person-groups.actions';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { CellTemplateContext, DatasetTableConfig, UiDatasetTableComponent, } from '@ui/lib/ui-kit/ui-dataset-table';
import { UiButtonComponent, UiModels, UiNoDataComponent } from '@ui';
import { UiPreloaderComponent } from '../../../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiSearchComponent } from '../../../../../../shared/ui-kit/ui-search/ui-search.component';
import { createSharedWithTableConfig } from './shared-with-table.util';
import openDialogByParams = UiModels.openDialogByParams;
import { routerSegments } from '@consts/routes';
import { Router } from '@angular/router';

@Component({
  selector: 'app-share-group-dialog',
  templateUrl: './share-group-dialog.component.html',
  styleUrl: './share-group-dialog.component.scss',
  imports: [
    TranslatePipe,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltip,
    UiButtonComponent,
    UiDatasetTableComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiSearchComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareGroupDialogComponent implements OnInit {
  protected readonly PersonGroupsModel = PersonGroupsModel;
  protected readonly loaderColor = PreloaderColor;
  protected readonly viewStates = SharedModels.ViewState;

  protected readonly shareDialogInitialEmpty = features.personGroups.shareDialog.initialEmpty;
  protected readonly shareDialogNoResults = features.personGroups.shareDialog.noResults;

  protected readonly actionsCellTemplate = viewChild.required<
    TemplateRef<CellTemplateContext<PersonGroupsModel.PersonGroupSharedWithRow>>
  >('actionsCell');

  private readonly store$ = inject(Store<AppState>);
  private readonly router = inject(Router);
  private readonly clipboard = inject(Clipboard);
  private readonly dialogRef = inject(MatDialogRef<ShareGroupDialogComponent>);
  private readonly activeOrgId = this.store$.selectSignal(OrganizationSelectors.selectOrgId);

  protected readonly memberSearch = signal('');
  protected readonly selectedAccessRole = signal<PersonGroupsModel.ShareAccessRole | null>(null);
  private readonly shareOptions = this.store$.selectSignal(personGroupsFeature.selectShareOptions);

  protected readonly personGroupAccessRoles = computed(() => PersonGroupsModel.SHARE_ACCESS_ROLES);
  protected readonly availableInviteRoles = computed(() =>
    PersonGroupsModel.invitableShareRolesForCaller(this.myShareAccessRole()),
  );
  protected readonly personGroupInviteCode = computed(() => this.shareOptions().inviteCode);
  protected readonly isInviteCodeLoading = computed(() => this.shareOptions().inviteCodeLoading);
  protected readonly personGroupAccessList = computed(() => this.shareOptions().accessList);
  protected readonly personGroupShareOptionsLoading = computed(() => {
    const so = this.shareOptions();
    return so.accessListLoading || (so.inviteCodeLoading && so.inviteCode == null);
  });

  protected readonly filteredSharedWithOrgs = computed(() => {
    const q = this.memberSearch().trim().toLowerCase();
    const rows = this.personGroupAccessList();
    if (!q) {
      return rows;
    }
    return rows.filter((r) => r.orgName.toLowerCase().includes(q));
  });

  protected readonly accessTypeSelectValue = computed(() => {
    const invitable = this.availableInviteRoles();
    const selected = this.selectedAccessRole();
    if (selected != null && invitable.includes(selected)) {
      return selected;
    }
    return invitable[0] ?? null;
  });

  protected readonly tableConfig = computed<DatasetTableConfig<PersonGroupsModel.PersonGroupSharedWithRow>>(() =>
    createSharedWithTableConfig({ actionsCell: this.actionsCellTemplate() }),
  );

  protected readonly viewState = computed(() => {
    const rows = this.filteredSharedWithOrgs();
    if (rows.length > 0) {
      return SharedModels.ViewState.HAS_CONTENT;
    }
    return this.hasActiveOrgSearch
      ? SharedModels.ViewState.NO_RESULTS
      : SharedModels.ViewState.INITIAL_EMPTY;
  });

  protected get hasActiveOrgSearch(): boolean {
    return this.memberSearch().trim().length > 0;
  }

  protected isUpdatingAccessForOrg(orgId: string): boolean {
    return this.shareOptions().updatingAccessOrgIds.includes(orgId);
  }

  /** Current org's role in this person group (access list + creator org). */
  protected readonly myShareAccessRole = computed((): PersonGroupsModel.ShareAccessRole | null => {
    const active = this.activeOrgId();
    const createdByOrgId = this.shareOptions().createdByOrgId ?? this.data.createdByOrgId;
    return PersonGroupsModel.resolveMyShareAccessRoleFromSharedWithRows(
      this.personGroupAccessList(),
      createdByOrgId,
      active,
    );
  });

  /** Invite access-type dropdown: disabled when caller cannot issue any invite role. */
  protected readonly isShareEditingLocked = computed(() => this.availableInviteRoles().length === 0);

  /**
   * Per-row role/remove: creator org row is never editable; other rows only for group Admins.
   */
  protected isShareEditingLockedForOrg(row: PersonGroupsModel.PersonGroupSharedWithRow): boolean {
    const active = this.activeOrgId();
    if (active == null || active === '') {
      return true;
    }
    const createdByOrgId = this.shareOptions().createdByOrgId ?? this.data.createdByOrgId;
    if (row.orgId === createdByOrgId) {
      return true;
    }
    const myRole = this.myShareAccessRole();
    return myRole == null || myRole !== PersonGroupsModel.ShareAccessRole.Admin;
  }

  public constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: PersonGroupsModel.ShareGroupDialogData,
  ) {}

  public ngOnInit(): void {
    this.store$.dispatch(
      PersonGroupsActions.startLoadingPersonGroupShareOptions({ groupId: this.data.groupId }),
    );
  }

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected onAccessTypeChange(event: MatSelectChange): void {
    if (this.isShareEditingLocked()) {
      return;
    }
    const value = event.value as string;
    if (!PersonGroupsModel.isShareAccessRole(value)) {
      return;
    }
    if (!this.availableInviteRoles().includes(value)) {
      return;
    }
    this.selectedAccessRole.set(value);
    this.store$.dispatch(
      PersonGroupsActions.setPersonGroupInviteCodeLoading({ personGroupInviteCodeLoading: true }),
    );
    this.store$.dispatch(
      PersonGroupsActions.loadPersonGroupInviteCode({
        groupId: this.data.groupId,
        accessType: value,
      }),
    );
  }

  protected onAccessListItemChange(
    row: PersonGroupsModel.PersonGroupSharedWithRow,
    event: MatSelectChange,
  ): void {
    if (this.isShareEditingLockedForOrg(row)) {
      return;
    }
    const value = event.value as string;
    if (!PersonGroupsModel.isShareAccessRole(value)) {
      return;
    }
    if (!this.availableInviteRoles().includes(value)) {
      return;
    }
    this.store$.dispatch(
      PersonGroupsActions.updatePersonGroupAccessListItem({
        groupId: this.data.groupId,
        orgId: row.orgId,
        accessType: value,
      }),
    );
  }

  protected removeOrgFromOption(
    row: PersonGroupsModel.PersonGroupSharedWithRow,
    rowRoleSelect: MatSelect,
    event: MouseEvent | KeyboardEvent,
  ): void {
    if (this.isShareEditingLockedForOrg(row)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.store$.dispatch(
      PersonGroupsActions.requestRemovePersonGroupAccess({
        groupId: this.data.groupId,
        orgId: row.orgId,
        orgName: row.orgName,
      }),
    );
    rowRoleSelect.close();
  }

  protected async shareCode(): Promise<void> {
    const code = this.personGroupInviteCode();
    if (!code || this.isInviteCodeLoading()) {
      return;
    }
    const url = this.buildShareUrl(code);
    if (typeof navigator === 'undefined' || !navigator.share) {
      this.clipboard.copy(url);
      return;
    }
    try {
      await navigator.share({ url });
    } catch {
      // User dismissed share sheet or share failed.
    }
  }

  protected copyInviteCode(): void {
    const code = this.personGroupInviteCode();
    if (!code || this.isInviteCodeLoading()) {
      return;
    }
    this.clipboard.copy(code);
  }

  private buildShareUrl(code: string): string {
    const tree = this.router.createUrlTree(
      ['/', routerSegments.database, routerSegments.personGroups],
      {
        queryParams: {
          [openDialogByParams.joinPersonGroup]: 'true',
          [PersonGroupsModel.JoinRouteQueryParam.InviteCode]: code,
        },
      },
    );
    const pathWithQuery = this.router.serializeUrl(tree);
    return (
      typeof window !== 'undefined' && window.location?.origin
        ? `${window.location.origin}${pathWithQuery}`
        : pathWithQuery
    );
  }
}
